import { WorkflowConfigInputModel, WorkflowOrderInputModel } from '@ail/orders-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useState } from 'react'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import {
  useGetOrdersFormsQuery,
  useGetWorkflowConfigQuery,
  usePostWorkflowConfigMutation,
  usePutWorkflowConfigMutation,
} from '../../../../redux/api/ordersApi'
import Alert from '../../../common/Alert/Alert'
import { LogisticIdsView } from './LogisticIdsView/LogisticIdsView'
import { WorkflowOrderConditionsEdit } from './WorkflowOrderConditionsEdit/WorkflowOrderConditionsEdit'
import { WorkflowOrderFormFieldsEdit } from './WorkflowOrderFormFieldsEdit/WorkflowOrderFormFieldsEdit'
import { WorkflowOrderFormEdit } from './WorkflowOrderFormEdit/WorkflowOrderFormEdit'
import './SupplierWorkflowEdit.scss'
import { WorkflowOrderListEdit } from './WorkflowOrderListEdit/WorkflowOrderListEdit'
import { Loader } from '../../../common/Loader/Loader'

export function SupplierWorkflowEdit() {
  const [editingConfig, setEditingConfig] = useState<WorkflowConfigInputModel>()
  const [selectedOrderIdx, setSelectedOrderIdx] = useState<number>(0)
  const [savingError, setSavingError] = useState<string>()
  const params = useParams<{ id: string }>()
  const history = useHistory()
  const orderForms = useGetOrdersFormsQuery()
  const workflowConfig = useGetWorkflowConfigQuery(params.id === 'new' ? skipToken : params.id)

  const [postWorkflowConfig, postState] = usePostWorkflowConfigMutation()
  const [putWorkflowConfig, putState] = usePutWorkflowConfigMutation()
  const isSaving = postState.isLoading || putState.isLoading

  useEffect(() => {
    if (params.id === 'new') {
      setEditingConfig({ name: '', orders: [] })
    } else {
      setEditingConfig(workflowConfig.currentData)
    }
    setSelectedOrderIdx(0)
  }, [params.id, workflowConfig.currentData])

  const involvedOrdersForms = useMemo(() => {
    if (params.id === 'new') return []
    return (
      orderForms.currentData?.filter(
        (f) => f.approvedOrderWorkflowId === params.id || f.returnOrderWorkflowId === params.id
      ) ?? []
    )
  }, [orderForms.currentData, params.id])

  const selectedOrder = useMemo(() => {
    return editingConfig?.orders.at(selectedOrderIdx)
  }, [editingConfig?.orders, selectedOrderIdx])

  const updateOrder = (orderIdx: number, updatedOrder: WorkflowOrderInputModel) => {
    if (!editingConfig) return
    const updatedConfig: WorkflowConfigInputModel = { ...editingConfig, orders: [...editingConfig.orders] }
    updatedConfig.orders[orderIdx] = updatedOrder
    setEditingConfig(updatedConfig)
  }

  const saveConfig = async () => {
    if (!editingConfig || isSaving) return
    try {
      if (params.id === 'new') {
        const saved = await postWorkflowConfig(editingConfig).unwrap()
        history.replace(`/RequestsManagement/EditSupplierWorkflowNew/${saved.id}`)
      } else {
        await putWorkflowConfig({ id: params.id, model: editingConfig }).unwrap()
      }
      alert('Workflow saved!')
    } catch {
      setSavingError('Failed to save workflow.')
    }
  }

  return (
    <Loader blocking={workflowConfig.isFetching || isSaving} className="workflow-config-edit">
      <div className="workflow-config-orders">
        {!!selectedOrder && !!editingConfig && (
          <div className="content-box">
            <div className="content-box-header">
              {selectedOrder.form.name}
              <NavLink to="/RequestsManagement/SupplierWorkflows" className="btn btnClose">
                <i className="icon-close" /> CANCEL
              </NavLink>
              <a className="btn btnSave" onClick={() => saveConfig()}>
                <i className="icon-check" /> SAVE
              </a>
            </div>
            <div className="content-box-body">
              <WorkflowOrderListEdit
                config={editingConfig}
                onChange={setEditingConfig}
                onSelectedOrderIdxChange={setSelectedOrderIdx}
                selectedOrderIdx={selectedOrderIdx}
              />
              <br />
              <WorkflowOrderFormEdit
                config={editingConfig}
                order={selectedOrder}
                onChange={(updated) => updateOrder(selectedOrderIdx, updated)}
              />
              <br />
              <WorkflowOrderFormFieldsEdit
                order={selectedOrder}
                onChange={(updated) => updateOrder(selectedOrderIdx, updated)}
              />
              <br />
              <WorkflowOrderConditionsEdit
                order={selectedOrder}
                forms={involvedOrdersForms}
                onChange={(updated) => updateOrder(selectedOrderIdx, updated)}
              />
              <br />
              <LogisticIdsView forms={involvedOrdersForms} />
            </div>
          </div>
        )}
      </div>
      <Alert
        visible={!!savingError}
        title="Error"
        message={savingError ?? ''}
        onClose={() => setSavingError(undefined)}
      />
    </Loader>
  )
}
